import { ArrowLeft } from 'lucide-react';
import { LEGAL_COMPANY, LEGAL_UPDATED_AT, LEGAL_VERSION, PRIVACY, TERMS, type LegalDocument } from '../legal/legalContent';

interface LegalPageProps {
    doc: 'terms' | 'privacy';
}

const DOCS: Record<LegalPageProps['doc'], LegalDocument> = { terms: TERMS, privacy: PRIVACY };

// Exibe os Termos de Contratação e a Política de Privacidade (textos em src/legal/legalContent.ts).
export function LegalPage({ doc }: LegalPageProps) {
    const content = DOCS[doc];
    const other = doc === 'terms'
        ? { href: '/privacidade', label: 'Política de Privacidade' }
        : { href: '/termos', label: 'Termos de Contratação' };

    const identification = [
        LEGAL_COMPANY.legalName || LEGAL_COMPANY.tradeName,
        LEGAL_COMPANY.cnpj && `CNPJ ${LEGAL_COMPANY.cnpj}`,
        LEGAL_COMPANY.address,
    ].filter(Boolean).join(' • ');

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans py-10 px-4 sm:px-6">
            <div className="mx-auto w-full max-w-3xl">
                <a href="/assinar" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                    <ArrowLeft size={16} /> Voltar para a contratação
                </a>

                <article className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 shadow-xl">
                    <img src="/logo-orion.svg" alt="Orion CRM" className="w-10 h-10 mb-6" />
                    <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">{content.title}</h1>
                    <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                        {identification} • Versão {LEGAL_VERSION} • Atualizado em {LEGAL_UPDATED_AT}
                    </p>
                    <p className="mt-6 text-slate-600 dark:text-slate-300 leading-relaxed">{content.intro}</p>

                    <div className="mt-8 space-y-8">
                        {content.sections.map((section, i) => (
                            <section key={section.title}>
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white">{i + 1}. {section.title}</h2>
                                <div className="mt-3 space-y-3 text-slate-600 dark:text-slate-300 leading-relaxed text-[15px]">
                                    {section.blocks.map((block, j) => typeof block === 'string'
                                        ? <p key={j}>{block}</p>
                                        : (
                                            <ul key={j} className="list-disc pl-6 space-y-1.5 marker:text-yellow-600">
                                                {block.list.map(item => <li key={item}>{item}</li>)}
                                            </ul>
                                        ))}
                                </div>
                            </section>
                        ))}
                    </div>

                    <p className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-500 dark:text-slate-400">
                        Leia também: <a href={other.href} className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">{other.label}</a>
                    </p>
                </article>
            </div>
        </div>
    );
}
